/**
 * External API helper - exposes subscription data to external scripts
 */

import { createApiGatewayClient } from '../services/apiGatewayClient';
import { loadConfig } from '../env/config_util';

/**
 * Check if external API is enabled in config
 */
function isExternalApiEnabled(): boolean {
  try {
    // Get brand from URL or default to geeni for dev
    const hostname = window.location.hostname;
    let configBrand = 'geeni'; // default

    // Simple brand detection from hostname
    if (hostname.includes('pepper')) {
      configBrand = 'pepper';
    } else if (hostname.includes('momentum')) {
      configBrand = 'momentum';
    } else if (hostname.includes('notion')) {
      configBrand = 'notion';
    }
    // Default to geeni for dev/merkury smart

    const config = loadConfig();
    const brandConfig = config.config_module[configBrand];

    return brandConfig?.externalApi?.enabled === true;
  } catch (error) {
    console.error('Failed to check external API config:', error);
    return false;
  }
}

/**
 * Validate if the current origin is allowed to access the external API
 */
function isOriginAllowed(): boolean {
  try {
    // Get brand from URL or default to geeni for dev (same logic as isExternalApiEnabled)
    const hostname = window.location.hostname;
    let configBrand = 'geeni'; // default

    // Simple brand detection from hostname
    if (hostname.includes('pepper')) {
      configBrand = 'pepper';
    } else if (hostname.includes('momentum')) {
      configBrand = 'momentum';
    } else if (hostname.includes('notion')) {
      configBrand = 'notion';
    }
    // Default to geeni for dev/merkury smart

    const config = loadConfig();
    const brandConfig = config.config_module[configBrand];
    const allowedOrigins = brandConfig?.externalApi?.allowedOrigins;

    if (!allowedOrigins || !Array.isArray(allowedOrigins)) {
      console.warn('[ExternalAPI] No allowedOrigins configured, denying access');
      return false;
    }

    const currentOrigin = window.location.origin;

    // Check if wildcard is allowed (for development)
    if (allowedOrigins.includes('*')) {
      console.log(`[ExternalAPI] Origin validation: ${currentOrigin} allowed (wildcard)`);
      return true;
    }

    // Check if current origin is in the allowed list
    const isAllowed = allowedOrigins.includes(currentOrigin);

    if (isAllowed) {
      console.log(`[ExternalAPI] Origin validation: ${currentOrigin} allowed`);
    } else {
      console.warn(`[ExternalAPI] Origin validation: ${currentOrigin} not in allowed origins:`, allowedOrigins);
    }

    return isAllowed;
  } catch (error) {
    console.error('[ExternalAPI] Origin validation failed:', error);
    return false;
  }
}

/**
 * Get authentication info from Redux store
 */
function getAuthInfo() {
  const store = (window as any).__REDUX_STORE__;
  if (!store) return null;

  const state = store.getState();
  const auth = state.authentication;
  const brand = state.brand?.brandName;

  if (!auth?.authenticated || !brand) return null;

  return {
    brand: brand,
    accountId: auth.profile?.pepperUser?.account_id,
    username: auth.username,
    isAuthenticated: auth.authenticated
  };
}

/**
 * Get subscription data from Redux store
 */
function getSubscriptionData() {
  const store = (window as any).__REDUX_STORE__;
  if (!store) return null;

  const state = store.getState();

  return {
    subscriptions: state.subscriptionList?.subscriptionPlans || [],
    paymentHistory: state.accountHistory?.accountHistoryList || [],
    devices: state.deviceList?.deviceList || []
  };
}

/**
 * Map brand names to config keys
 */
function mapBrandName(brandName: string): string {
  const brandMap: Record<string, string> = {
    'merkury smart': 'geeni',
    'geeni': 'geeni',
    'pepper': 'pepper',
    'momentum': 'momentum',
    'notion': 'notion'
  };

  const mapped = brandMap[brandName.toLowerCase()];
  if (!mapped) {
    console.warn(`Unknown brand: ${brandName}, defaulting to geeni`);
    return 'geeni';
  }

  return mapped;
}

/**
 * Make authenticated API calls using the existing AWS API Gateway client
 */
async function makeAuthenticatedApiCall(
  endpoint: string,
  method: string = 'GET',
  body?: any,
  context: string = 'subscriptions'
): Promise<any> {
  // Validate origin before making API calls
  if (!isOriginAllowed()) {
    throw new Error('API access denied: origin not in allowedOrigins');
  }

  const store = (window as any).__REDUX_STORE__;
  if (!store) {
    throw new Error('Redux store not available');
  }

  const state = store.getState();
  const auth = state.authentication;
  const brandName = state.brand?.brandName;

  if (!auth?.authenticated || !auth.profile) {
    throw new Error('User is not authenticated');
  }

  if (!auth.profile.pepperUser?.awsUserCredentials) {
    throw new Error('AWS credentials not available');
  }

  try {
    // Map the brand name to the config key
    const configBrand = mapBrandName(brandName);

    console.log(`[ExternalAPI] ${method} ${endpoint} | brand="${brandName}" -> config="${configBrand}" | context="${context}"`);

    // Create API Gateway client using the same method as your existing code
    const apiGatewayClient = createApiGatewayClient(
      auth.profile.pepperUser.awsUserCredentials,
      configBrand,
      context
    );

    const pathTemplate = endpoint;
    const params = {};
    const additionalParams = {
      headers: {
        peppertoken: auth.profile.token,
      },
      queryParams: {},
    };

    // Use the API Gateway client to make the call
    const result = await apiGatewayClient.invokeApi(
      params,
      pathTemplate,
      method.toUpperCase(),
      additionalParams,
      body || {}
    );

    return result.data;

  } catch (error) {
    console.error('Authenticated API call failed:', error);
    throw error;
  }
}

/**
 * Expose simple API to window
 */
export function exposeSubscriptionApi() {
  if (typeof window !== 'undefined') {
    // Check if external API is enabled in config
    if (!isExternalApiEnabled()) {
      console.log('External API is disabled in config');
      return;
    }

    // Validate origin is allowed to access the API
    if (!isOriginAllowed()) {
      console.warn('External API access denied: origin not in allowedOrigins');
      return;
    }

    // Only expose Redux store when external API is enabled
    const store = (window as any).__REDUX_STORE__;
    if (!store) {
      // Import store dynamically to avoid circular dependencies
      import('../store').then(storeModule => {
        (window as any).__REDUX_STORE__ = storeModule.default;
        console.log('Redux store exposed for external API');
      });
    }
    (window as any).SubscriptionAPI = {
      // Check if user is authenticated
      isAuthenticated: () => {
        if (!isOriginAllowed()) {
          console.warn('[ExternalAPI] isAuthenticated: origin not allowed');
          return false;
        }
        const auth = getAuthInfo();
        return !!auth;
      },

      // Get user info
      getUserInfo: () => {
        if (!isOriginAllowed()) {
          console.warn('[ExternalAPI] getUserInfo: origin not allowed');
          return null;
        }
        return getAuthInfo();
      },

      // Get subscription data
      getSubscriptions: () => {
        if (!isOriginAllowed()) {
          console.warn('[ExternalAPI] getSubscriptions: origin not allowed');
          return [];
        }
        const data = getSubscriptionData();
        return data?.subscriptions || [];
      },

      // Get payment history
      getPaymentHistory: () => {
        if (!isOriginAllowed()) {
          console.warn('[ExternalAPI] getPaymentHistory: origin not allowed');
          return [];
        }
        const data = getSubscriptionData();
        return data?.paymentHistory || [];
      },

      // Get devices
      getDevices: () => {
        if (!isOriginAllowed()) {
          console.warn('[ExternalAPI] getDevices: origin not allowed');
          return [];
        }
        const data = getSubscriptionData();
        return data?.devices || [];
      },

      // Make authenticated API calls
      apiCall: async (endpoint: string, method: string = 'GET', body?: any, context: string = 'subscriptions') => {
        return makeAuthenticatedApiCall(endpoint, method, body, context);
      },

      // Convenience methods for common authenticated API calls
      refreshSubscriptions: async (accountId?: string) => {
        // This endpoint has CORS issues, so return current Redux state
        console.log('[ExternalAPI] refreshSubscriptions: Using Redux state due to CORS issues');
        const data = getSubscriptionData();
        return {
          subscriptions: data?.subscriptions || [],
          trialEligibility: {},
          source: 'redux_state'
        };
      },

      refreshPaymentHistory: async () => {
        try {
          const result = await makeAuthenticatedApiCall('/paymentHistory', 'GET', null, 'subscriptions');
          return { ...result, source: 'api_call' };
        } catch (error) {
          console.log('[ExternalAPI] refreshPaymentHistory: Falling back to Redux state');
          const data = getSubscriptionData();
          return { paymentHistory: data?.paymentHistory || [], source: 'redux_fallback' };
        }
      },

      getAvailablePlans: async () => {
        try {
          const result = await makeAuthenticatedApiCall('/subscriptions', 'GET', null, 'account');
          return { ...result, source: 'api_call' };
        } catch (error) {
          console.log('[ExternalAPI] getAvailablePlans: Falling back to Redux state');
          const data = getSubscriptionData();
          return { plans: data?.subscriptions || [], source: 'redux_fallback' };
        }
      },

      // Subscription Management Operations
      pauseSubscription: async (subscriptionId: string) => {
        if (!subscriptionId) throw new Error('Subscription ID is required');
        return makeAuthenticatedApiCall(`/${subscriptionId}/pause`, 'POST', {}, 'subscriptions');
      },

      resumeSubscription: async (subscriptionId: string) => {
        if (!subscriptionId) throw new Error('Subscription ID is required');
        return makeAuthenticatedApiCall(`/${subscriptionId}/resume`, 'POST', {}, 'subscriptions');
      },

      cancelSubscription: async (subscriptionId: string) => {
        if (!subscriptionId) throw new Error('Subscription ID is required');
        return makeAuthenticatedApiCall(`/${subscriptionId}`, 'DELETE', {}, 'subscriptions');
      },

      applyCoupon: async (subscriptionId: string, couponId: string, currency: string = 'USD') => {
        if (!subscriptionId) throw new Error('Subscription ID is required');
        if (!couponId) throw new Error('Coupon ID is required');

        const body = {
          coupon_id: couponId,
          currency: currency
        };

        return makeAuthenticatedApiCall(`/${subscriptionId}/couponRedemptions`, 'POST', body, 'subscriptions');
      },

      extendSubscription: async (subscriptionId: string, extensionData: any) => {
        if (!subscriptionId) throw new Error('Subscription ID is required');
        return makeAuthenticatedApiCall(`/${subscriptionId}/extensions`, 'POST', extensionData, 'subscriptions');
      }
    };

    console.log('SubscriptionAPI exposed to window with authenticated API calls');
  }
}

/**
 * Remove external API and clean up window exposure
 */
export function removeSubscriptionApi() {
  if (typeof window !== 'undefined') {
    // Remove the API
    delete (window as any).SubscriptionAPI;

    // Remove the Redux store exposure
    delete (window as any).__REDUX_STORE__;

    console.log('External API and Redux store removed from window');
  }
}
